// The audio director's arithmetic, kept apart from the WebAudio glue so it
// pins down in a node test: how loud and how far left a world-space sound
// is, and where a playlist cursor goes next. Nothing here touches the DOM,
// an AudioContext or a clock, and every random draw arrives as an `rng`
// parameter so a seeded test gets the same answer twice.

// The model's figures ride presentation.toml [presentation.camera] and
// [presentation.audio] (R312).
import {
  AUDIO_GAIN_CUTOFF_R, AUDIO_PAN_MAX, AUDIO_ZOOM_GAIN_FLOOR, CAMERA_MIN_ZOOM,
} from '../gen/presentation';
import type { SfxVoice } from './manifest';
import { clamp01 } from '../render/math';

/** The far end of the zoom loudness ramp. It is the camera's own
 *  min_zoom: one entry, read here rather than mirrored (R312). */
export const MIN_ZOOM = CAMERA_MIN_ZOOM;

/** How many viewport half-diagonals out a sound stays audible. At 1 it is
 *  at the edge of the screen and still full; at this multiple it is silent. */
export const GAIN_CUTOFF_R = AUDIO_GAIN_CUTOFF_R;

/** What a fully zoomed-out map keeps of a sound's loudness. Zoomed out is
 *  quieter on purpose: the viewport is wide, so far more of the map is
 *  inside the audible circle at once. */
export const ZOOM_GAIN_FLOOR = AUDIO_ZOOM_GAIN_FLOOR;

/** The widest stereo placement a sound gets. A hard ±1 pan puts a torpedo
 *  in one ear only, which reads as a fault rather than as a direction. */
export const PAN_MAX = AUDIO_PAN_MAX;

/**
 * How a cue treats its voice line. `true` speaks where the one spoken
 * channel is free, `false` plays the effect alone (R595), and `'priority'`
 * cuts the line that speaks and speaks at once (audio.md §5.3, R1148). The
 * priority is one flag and not a ranking: nothing is queued, and a normal
 * line that arrives while a priority line speaks is still dropped.
 */
export type VoiceMode = boolean | 'priority';

/** The viewport in world pixels, as main.ts already builds it per frame. */
export interface ViewRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * How loud a sound at world position (x, y) is for a viewer looking at
 * `view` at this `zoom`. 0 means "do not play it at all".
 *
 * Anything on screen is at full distance gain: the audible circle is the
 * viewport's half-diagonal R, and it fades to nothing at GAIN_CUTOFF_R · R.
 * R is world-space, so zooming out already widens the circle; the zoom term
 * is the separate, explicit "zoomed out is quieter" rule. The square on the
 * distance term makes the falloff read as distance rather than as a fader.
 */
export function positionalGain(x: number, y: number, view: ViewRect, zoom: number): number {
  const cx = (view.x0 + view.x1) / 2;
  const cy = (view.y0 + view.y1) / 2;
  const r = Math.hypot(view.x1 - view.x0, view.y1 - view.y0) / 2;
  if (!(r > 0)) return 0;
  const d = Math.hypot(x - cx, y - cy);
  const base = d <= r
    ? 1
    : Math.max(0, (GAIN_CUTOFF_R * r - d) / ((GAIN_CUTOFF_R - 1) * r));
  const zoomF = ZOOM_GAIN_FLOOR
    + (1 - ZOOM_GAIN_FLOOR) * clamp01((zoom - MIN_ZOOM) / (1 - MIN_ZOOM));
  return base * base * zoomF;
}

/**
 * Where a sound at world x sits across the stereo field. `viewX` and `zoom`
 * are the world container's own transform, so this is the same screen-space
 * mapping the renderer uses, and `screenW` the canvas width in CSS pixels.
 */
export function panOf(worldX: number, viewX: number, zoom: number, screenW: number): number {
  if (!(screenW > 0)) return 0;
  const screenX = worldX * zoom + viewX;
  const t = (2 * screenX) / screenW - 1;
  return PAN_MAX * (t < -1 ? -1 : t > 1 ? 1 : t);
}

/**
 * A shuffled play order over `[0, len)` with `current` first, so switching
 * shuffle on does not interrupt the track that is playing. Fisher-Yates
 * over every index, then a rotation — a rotation keeps the draw uniform,
 * where a swap would bias whatever landed in slot 0.
 */
export function makeShuffle(len: number, current: number, rng: () => number): number[] {
  if (len <= 0) return [];
  const out: number[] = [];
  for (let i = 0; i < len; i++) out.push(i);
  for (let i = len - 1; i > 0; i--) {
    const j = Math.floor(clamp01(rng()) * (i + 1));
    const t = out[i];
    out[i] = out[j];
    out[j] = t;
  }
  const at = out.indexOf(current);
  if (at <= 0) return out;
  return out.slice(at).concat(out.slice(0, at));
}

/**
 * The two gains of a crossfade at `t`, from 0 at the start of the window to
 * 1 at its end: `[outgoing, incoming]` (audio.md §3.9).
 *
 * The curve is equal-power. cos² + sin² = 1 at every point, so the two beds
 * sum to the loudness of one and the mix neither clips at the ends nor dips
 * in the middle — a linear pair would lose 3 dB halfway across. t = 0 gives
 * (1, 0), t = 0.5 gives (√½, √½), and t = 1 gives (0, 1).
 */
export function crossfadeGains(t: number): [number, number] {
  const u = clamp01(t) * (Math.PI / 2);
  return [Math.cos(u), Math.sin(u)];
}

/** How many points a scheduled crossfade curve carries. It is the sample
 *  count of a shape, not a tuning figure: 64 steps over a window of seconds
 *  is far below what an ear resolves, and the array is built once per
 *  track change. */
export const CROSSFADE_STEPS = 64;

/**
 * One half of a crossfade as a `setValueCurveAtTime` array. `side` 0 is the
 * outgoing deck and 1 the incoming one, and `from` is the gain the outgoing
 * deck holds at the start — 1 for a track that ran to its window, and less
 * where a second track change re-targets a fade already in flight. Scaling
 * the outgoing half by `from` keeps the sum of squares at or under 1, so a
 * re-target cannot clip either.
 */
export function crossfadeCurve(side: 0 | 1, from = 1): Float32Array {
  const out = new Float32Array(CROSSFADE_STEPS);
  for (let i = 0; i < CROSSFADE_STEPS; i++) {
    const g = crossfadeGains(i / (CROSSFADE_STEPS - 1));
    out[i] = side === 0 ? g[0] * from : g[1];
  }
  return out;
}

/** The next cursor, wrapping at the end — a playlist loops, it does not stop. */
export function nextIndex(cur: number, len: number): number {
  if (len <= 0) return 0;
  return ((cur + 1) % len + len) % len;
}

/** The previous cursor, wrapping at the start. */
export function prevIndex(cur: number, len: number): number {
  if (len <= 0) return 0;
  return ((cur - 1) % len + len) % len;
}

/**
 * How long after the effect starts a voice line starts, in seconds.
 *
 * `after` puts the line behind the effect instead of over it, so the two are
 * never said at once; `delay` is the gap on top of that, in milliseconds and
 * never negative. The theme carries the defaults and one id may override
 * either half. `effectDuration` is null where the id has no effect at all,
 * and an `after` with nothing to wait for is a plain delay.
 */
export function voiceStartOffset(
  theme: SfxVoice,
  id: string,
  effectDuration: number | null,
): number {
  const t = theme.timing[id];
  const after = t?.after ?? theme.after;
  const delay = t?.delay ?? theme.delay;
  const wait = after && effectDuration !== null ? effectDuration : 0;
  return wait + delay / 1000;
}

/**
 * The voice-line urls one cue speaks in `locale` (`audio.md` §5.4, R1145).
 * The map of that language wins where it holds the id; otherwise the
 * English `map` speaks, and an id neither holds speaks nothing (undefined).
 * English is the source language, so its tag reads `map` directly.
 */
export function voiceLinesFor(
  voice: SfxVoice,
  id: string,
  locale: string,
): string[] | undefined {
  const own = voice.locales?.[locale]?.[id];
  if (own && own.length > 0) return own;
  return voice.map[id];
}

/**
 * Which variant of a trigger to play, given the one played last. Repeating
 * the same clip twice is what makes a sound bank sound like a sound bank,
 * so an immediate repeat is excluded whenever there is anything else to
 * pick. Returns -1 when the trigger has no variants at all.
 */
export function pickVariant(count: number, last: number, rng: () => number): number {
  if (count <= 0) return -1;
  if (count === 1) return 0;
  const r = clamp01(rng());
  if (last < 0 || last >= count) return Math.min(count - 1, Math.floor(r * count));
  const i = Math.min(count - 2, Math.floor(r * (count - 1)));
  return i >= last ? i + 1 : i;
}
